import useArtwork from '@hooks/useArtwork'
import useCampaign, { useCampaignForm } from '@hooks/useCampaign'

import type { EntityOptions } from '@redux/modules/module'

type AgencyMarketingPayload = {
  agency_id: number,
  artwork_page: {
    template_page_id: number,
  },
  order: {
    order_type: 'item',
    item: {
      price_list_item: {
        id: number,
      },
      options: EntityOptions,
    },
  },
  template_page_category_id: number,
}

type UseAgencyMarketingCampaignParams = {
  categoryId: number,
  selectedAgencyId: number,
  selectedCampaignId?: number | null,
  selectedProviderPriceListItemId: number,
  selectedProviderPriceListItemOptions: number,
  selectedTemplatePageId: number,
}

const useAgencyMarketingCampaign = (params: UseAgencyMarketingCampaignParams) => {
  const {
    categoryId,
    selectedAgencyId,
    selectedCampaignId,
    selectedProviderPriceListItemId,
    selectedProviderPriceListItemOptions,
    selectedTemplatePageId,
  } = params || {}

  const artworkPayload = useArtwork()
  const {
    callbacks: {
      createArtworkWithCampaign,
    },
    creating: creatingArtwork,
  } = artworkPayload

  // For Creating Artwork with Existing Campaign
  // @ts-expect-error TS2322
  const campaignPayload = useCampaign({ id: selectedCampaignId })
  const {
    callbacks: { createCampaign },
    campaign,
    creating: creatingCampaign,
  } = campaignPayload

  // For Creating Artwork with a new Campaign
  const campaignFormPayload = useCampaignForm({
    ...campaign,
    agency_id: selectedAgencyId,
    agency_marketing: true,
  })
  const {
    entityState: {
      agency_id,
      agent_ids,
      title,
    },
  } = campaignFormPayload

  // Custom campaign required fields/Save Enabled for Agency Marketing Campaign
  const saveEnabled = !!agency_id && !!agent_ids?.length && !!title

  const agencyMarketingPayload: AgencyMarketingPayload = {
    agency_id: selectedAgencyId,
    artwork_page: {
      template_page_id: selectedTemplatePageId,
    },
    order: {
      order_type: 'item',
      item: {
        price_list_item: {
          id: selectedProviderPriceListItemId,
        },
        // @ts-expect-error TS2322
        options: JSON.stringify(selectedProviderPriceListItemOptions),
      },
    },
    template_page_category_id: categoryId,
  }

  return {
    agencyMarketingPayload,
    callbacks: {
      createArtworkWithCampaign,
      createCampaign,
    },
    campaignPayload,
    campaignFormPayload,
    creatingArtwork,
    creatingCampaign,
    saveEnabled,
    selectedAgencyId,
  }
}

export type AgencyMarketingCampaignPayload = ReturnType<typeof useAgencyMarketingCampaign>

export default useAgencyMarketingCampaign
